import { useState, useRef, useEffect, useCallback } from 'react';
import useAppRouting from './useAppRouting';

function useAppViewState({
  resetConversationState,
  setSessionActive,
}) {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [overlayAnchor, setOverlayAnchor] = useState(null);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const selectedProfileRef = useRef(selectedProfile);

  useEffect(() => {
    selectedProfileRef.current = selectedProfile;
  }, [selectedProfile]);

  const handleRouteStateChange = useCallback((state, options = {}) => {
    const { isInitialLoad = false, updateUrl } = options;
    const normalizedOverlay = state.overlay === 'counsellors' ? 'counsellor-freeform' : state.overlay;

    if (state.customBuilder) {
      setShowCustomBuilder(true);
      setActiveOverlay(null);
      setOverlayAnchor(null);
      if (isInitialLoad && updateUrl) {
        updateUrl(null, null, true, true);
      }
      return;
    }

    setShowCustomBuilder(false);
    setActiveOverlay(normalizedOverlay);
    setOverlayAnchor(null);

    if (state.clientId !== null && state.clientId !== selectedProfileRef.current) {
      setSelectedProfile(state.clientId);
      setSessionActive(true);
      resetConversationState();
    }

    if (isInitialLoad && updateUrl && (normalizedOverlay || state.clientId !== null)) {
      updateUrl(normalizedOverlay, state.clientId, true);
    }
  }, [resetConversationState, setSessionActive]);

  const { updateUrl } = useAppRouting({
    onRouteStateChange: handleRouteStateChange,
  });

  const openOverlay = useCallback((overlayType, anchor = null) => {
    const normalizedOverlay = overlayType === 'counsellors' ? 'counsellor-freeform' : overlayType;
    setActiveOverlay(normalizedOverlay);
    setOverlayAnchor(anchor);
    setShowCustomBuilder(false);
    updateUrl(normalizedOverlay, null);
  }, [updateUrl]);

  const closeOverlay = useCallback((resetClientProfileVisibility) => {
    setActiveOverlay(null);
    setOverlayAnchor(null);
    resetClientProfileVisibility?.();
    updateUrl(null, selectedProfileRef.current);
  }, [updateUrl]);

  const showCustomBuilderView = useCallback(() => {
    setShowCustomBuilder(true);
    setActiveOverlay(null);
    setOverlayAnchor(null);
    updateUrl(null, null, false, true);
  }, [updateUrl]);

  const hideCustomBuilderView = useCallback((clientId = null) => {
    setShowCustomBuilder(false);
    setOverlayAnchor(null);
    updateUrl(null, clientId);
  }, [updateUrl]);

  const showHomeView = useCallback(() => {
    setShowCustomBuilder(false);
    setActiveOverlay(null);
    setOverlayAnchor(null);
    updateUrl(null, null);
  }, [updateUrl]);

  return {
    selectedProfile,
    setSelectedProfile,
    activeOverlay,
    overlayAnchor,
    showCustomBuilder,
    setActiveOverlay,
    openOverlay,
    closeOverlay,
    showCustomBuilderView,
    hideCustomBuilderView,
    showHomeView,
    updateUrl,
  };
}

export default useAppViewState;
